/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useMemo } from 'react';
import { Typography } from '../../../atoms';
import {
	IExpandedSlot,
	IPopupRedirections,
	ITimeSlot,
	PaymentStatusEnum,
	PrivacySettingsEnum,
	ReservationPaymentStatusExtendedEnum,
	SlotTypeEnum,
} from '../types';
import { ETypography, ETypographyColor, IColors, Icons } from '../../../types';
import { IdHyperlink } from '../../../molecules/IdHyperlink';
import { BnIcon } from '../../../components/icons_v2';
import { css, jsx } from '@emotion/react';
import { ETimeFormat, formatTime } from '../../../calendar/utils/timeUtils';
import { flexCenterCss, flexRowCss, flexWrapCss, gapCss } from '../../../styles/utils';
import { ETagAllowedColors } from '../../../components/Tag/types';
import { useColors } from '../../../hooks/useColors';
import { Tag } from '../../../components/Tag';
import { Tooltip } from '../../../components/Tooltip';
import { ReservationHistoryLink } from './ReservationHistoryLink';

const slotHeader = (colors: IColors) => css`
	display: flex;
	flex-direction: column;
	padding: 0.4rem 2rem 1.3rem 2rem;
	gap: 0.6rem;
	border-bottom: 0.1rem solid ${colors.bg_border_seperator};
`;

const titleRowCss = css`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 0.6rem;
	min-height: 2.4rem;
	min-width: 0;
`;

const titleCss = css`
	display: flex;
	flex: 0 1 auto;
	min-width: 0;
	align-items: center;
	h4 {
		display: block;
		margin: 0;
		min-width: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

const titleActionsCss = css`
	display: flex;
	align-items: center;
	flex-shrink: 0;
	gap: 0.2rem;
`;

const reservationDetails = css`
	display: flex;
	gap: 0.6rem;
	position: relative;
	justify-content: space-between;
	flex-wrap: wrap;
`;

const conflictCss = (colors: IColors) => css`
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.4rem;
	height: 2.4rem;
	padding: 0;
	border: none;
	background: none;
	color: ${colors.tx_error};
	svg {
		width: 2rem;
		height: 2rem;
	}
`;

export interface IHeaderLabels {
	reservation: string;
	conflicts: string;
	reservationHistory: string;
	viewHistory: string;
	viewConflicts: string;
	ariaLabel: {
		conflicts: string;
		reservationId: string;
		reservationHistory: string;
	};
	tags: {
		customer: string;
		punchPass: string;
		maintenance: string;
		internal: string;
		paid: string;
		unpaid: string;
		partiallyPaid: string;
		refunded: string;
		voided: string;
		hold: string;
		private: string;
	};
	occurenceCount: (index: number, totalCount: number) => string;
	idHyperlink: {
		id: (id: number) => string;
	};
}

export const Header = ({
	expandedSlot,
	redirections,
	labels,
}: {
	expandedSlot: IExpandedSlot;
	redirections?: IPopupRedirections;
	labels: IHeaderLabels;
}) => {
	const { colors } = useColors();

	const ReservationTag = () => {
		const goToReservation = redirections?.goToReservation;
		return expandedSlot?.reservationId ? (
			<div css={[flexRowCss, flexCenterCss, gapCss(4)]}>
				<Typography color={ETypographyColor.secondary} type={ETypography.body2}>
					{labels.reservation}
				</Typography>
				<IdHyperlink
					id={expandedSlot.reservationId}
					isHyperlink={!!goToReservation}
					onClick={() => goToReservation?.(expandedSlot.reservationId)}
					aria-label={labels.ariaLabel.reservationId}
					labels={labels.idHyperlink}
				/>
			</div>
		) : null;
	};

	const SlotOccurrenceCount = () => {
		return expandedSlot?.occurrence && expandedSlot?.totalSlotsInReservation ? (
			<Typography color={ETypographyColor.secondary} type={ETypography.caption}>
				{labels.occurenceCount(expandedSlot.occurrence, expandedSlot.totalSlotsInReservation)}
			</Typography>
		) : null;
	};

	const ReservationHistory = () => {
		const goToReservationHistory = redirections?.goToReservationHistory;
		const reservationCreatedAt = expandedSlot?.reservation?.createdAt;
		if (!goToReservationHistory || !expandedSlot?.reservationId || !reservationCreatedAt) {
			return null;
		}

		return (
			<ReservationHistoryLink
				reservationId={expandedSlot.reservationId}
				reservationCreatedAt={reservationCreatedAt}
				ariaLabel={labels.ariaLabel.reservationHistory}
				tooltip={labels.viewHistory}
				onOpen={goToReservationHistory}
			/>
		);
	};

	const Conflicts = () => {
		const goToConflicts = redirections?.goToConflicts;
		const handleGoToConflicts = () => {
			if (!expandedSlot || expandedSlot?.spaceId === undefined) return;
			const timeSlot: ITimeSlot = {
				resourceId: expandedSlot.spaceId,
				resourceName: expandedSlot?.resource?.name || '',
				startTime: expandedSlot.startTime,
				endTime: expandedSlot.endTime,
				startDate: formatTime(expandedSlot.startDate, ETimeFormat.FULL_DATE),
				endDate: formatTime(expandedSlot.endDate, ETimeFormat.FULL_DATE),
				relevantSlotId: expandedSlot.id.toString(),
			};
			goToConflicts && goToConflicts(timeSlot);
		};

		const conflictsStyle = useMemo(() => conflictCss(colors), [colors]);
		return (
			<div>
				{expandedSlot?.conflictCount !== undefined && expandedSlot.conflictCount > 0 && (
					<Tooltip content={labels.viewConflicts} isTopLayer placement="top-center">
						<button
							type="button"
							data-aid="popupBody-conflicts-button"
							css={conflictsStyle}
							aria-label={labels.ariaLabel.conflicts}
							onClick={handleGoToConflicts}
						>
							<BnIcon icon={Icons.conflict} />
						</button>
					</Tooltip>
				)}
			</div>
		);
	};

	const Tags = () => {
		if (!expandedSlot) return null;

		const slotTypeMap = {
			[SlotTypeEnum.EXTERNAL]: { title: labels.tags.customer, color: ETagAllowedColors.Pink },
			[SlotTypeEnum.PUNCH_PASS]: { title: labels.tags.punchPass, color: 'teal' },
			[SlotTypeEnum.MAINTENANCE]: expandedSlot.parentSlotId
				? { title: labels.tags.maintenance, color: ETagAllowedColors.Gray }
				: null,
			[SlotTypeEnum.INTERNAL]: { title: labels.tags.internal, color: ETagAllowedColors.Purple },
		};

		const paymentStatusMap = {
			[PaymentStatusEnum.FULLY_PAID]: { title: labels.tags.paid, color: ETagAllowedColors.Green },
			[PaymentStatusEnum.NOT_PAID]: { title: labels.tags.unpaid, color: ETagAllowedColors.Red },
			[PaymentStatusEnum.PARTIAL_PAYMENT]: { title: labels.tags.partiallyPaid, color: ETagAllowedColors.Yellow },
			[PaymentStatusEnum.REFUNDED]: { title: labels.tags.refunded, color: ETagAllowedColors.Blue },
			[PaymentStatusEnum.VOID]: { title: labels.tags.voided, color: ETagAllowedColors.Gray },
			[ReservationPaymentStatusExtendedEnum.PURCHASE_ORDER]: {
				title: labels.tags.hold,
				color: ETagAllowedColors.Yellow,
			},
		};

		const tags = [
			slotTypeMap[expandedSlot.slotType],
			paymentStatusMap[expandedSlot.paymentStatus],
			expandedSlot.privacySetting === PrivacySettingsEnum.PRIVATE && { title: labels.tags.private, color: 'orange' },
		].filter(Boolean);

		return (
			<div css={[flexRowCss, flexCenterCss, gapCss(4)]}>
				{tags.map(({ title, color }) => (
					<Tag key={title} title={title} color={color} />
				))}
			</div>
		);
	};

	const slotHeaderStyle = useMemo(() => slotHeader(colors), [colors]);
	return (
		<div data-aid="popupBody-header" css={slotHeaderStyle}>
			<div data-aid="popupBody-title" css={titleRowCss}>
				<div css={titleCss} data-testid="slot-popup-title">
					{expandedSlot?.title ? (
						<Typography color={ETypographyColor.primary} type={ETypography.h4} title={expandedSlot.title}>
							{expandedSlot.title}
						</Typography>
					) : null}
				</div>
				<div css={titleActionsCss} data-testid="slot-popup-title-actions">
					<ReservationHistory />
					<Conflicts />
				</div>
			</div>
			<div data-aid="popupBody-reservationDetails" css={reservationDetails}>
				<div css={[flexWrapCss, flexCenterCss, gapCss(8)]}>
					<ReservationTag />
					<SlotOccurrenceCount />
					<Tags />
				</div>
			</div>
		</div>
	);
};
